import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from '@thd-olt-component-react/router';
import {
  useHelmet
} from '@thd-nucleus/thd-helmet';
import {
  useDataModel,
  extend
} from '@thd-nucleus/data-sources';
import { useStoreId, useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';

import {
  buildAndroidAlternateTag,
  extractNValue,
  getNormalizedNavParam,
  parseCrumbs,
  processBreadcrumbs,
  decodeComparisonSymbols
} from '../../helpers/browse-helpers';
import {
  OPEN_GRAPH_FACEBOOK_ADMIN,
  HD_URL
} from '../../constants';
import {
  getBrowseSearchDescription,
  getBrowseSearchTitle,
  getCanonicalUrlForPriceRange,
  getRobotsTag,
  getSchemaTag,
  getStandAloneBrandPageCrumbs,
  getType,
  getKeyword
} from './browse-search-metadata-helpers';
import {
  removeTrailingNewline,
  safeJsonParse,
  generateTags,
  isCanonicalUrlValid,
  isObjectValid,
  isTitleValid,
  getPreferredValue
} from '../../utils';
import {
  browseSearchModel,
  emtContentModel
} from '../../models';
import { filterEmtMetadataOverrides } from '../../helpers/emt-content-helpers';

// Handler function to generate meta tags
const browseHandler = ({ data = {}, props = {} }) => {
  const { emtData, location, isSearchPageIndexingEnabled, searchModel = {} } = data;
  const emtDataContent = emtData?.emtContent?.content;
  const emtMetadata = getPreferredValue(emtDataContent?.metadata, {});

  if (!isObjectValid(searchModel) && !isObjectValid(emtMetadata)) return null;

  const {
    searchReport, metadata, taxonomy, templates, products
  } = searchModel;

  const { pathWithQueryParams, isTableView } = props;

  // templates[0] may be a JSON string or an object
  const parsedTemplates = safeJsonParse(templates?.[0]);

  const nValue = extractNValue(metadata?.canonicalUrl);
  const { keyword: searchReportKeyword } = searchReport || {};

  const pageType = pathWithQueryParams?.includes('/b/') ? 'b' : 's';
  const crumbs = getPreferredValue(taxonomy?.breadCrumbs, []);
  const [categories, refinements] = processBreadcrumbs(crumbs);

  const isContentPageOnly = parsedTemplates?.options?.content_type === 'categorypage';
  const isHybrid = metadata?.hasPLPBanner;
  const isBrandPage = taxonomy?.breadCrumbs?.some((crumb) => crumb.dimensionName === 'Brand');
  const isStandAloneBrandPage = crumbs?.length === 1 && crumbs?.[0]?.dimensionName.toLowerCase() === 'brand';

  // ---- Canonical resolution  ---- //
  const defaultCanonical = `${HD_URL}${metadata?.canonicalUrl}`;
  const priceRangeRefinement = emtDataContent?.priceRangeRefinement;
  const priceRangeExceptionCanonical = getCanonicalUrlForPriceRange(priceRangeRefinement, location, defaultCanonical);
  const searchIndexingCanonical = isSearchPageIndexingEnabled
    ? emtDataContent?.searchPageIndexing?.canonicalUrl
    : null;

  const canonical = priceRangeExceptionCanonical || searchIndexingCanonical || defaultCanonical;

  const isCanonicalValid = isCanonicalUrlValid(canonical);

  // Stand-Alone Brand Pages override the schema breadcrumb trail with Home > Brands > <Brand>,
  // built from the raw brand crumb (parseCrumbs would otherwise drop it, since a real
  // single-Brand crumb always carries a deselectUrl). Every other page type gets the normally
  // parsed/filtered breadcrumb trail.
  const schemaBreadcrumbs = isStandAloneBrandPage
    ? getStandAloneBrandPageCrumbs(crumbs, canonical)
    : parseCrumbs(crumbs, searchReportKeyword);

  // ---- Fallback Title, Description, and Type ---- //
  const parsedTemplatesDescription = parsedTemplates?.options?.metaData?.description;
  const fallbackDescription = getBrowseSearchDescription({
    pageType,
    categories,
    refinements,
    searchReportKeyword,
    isContentPageOnly,
    parsedTemplatesDescription
  });
  const parsedTemplatesTitle = parsedTemplates?.options?.metaData?.title;
  const fallbackTitle = getBrowseSearchTitle({
    isContentPageOnly,
    parsedTemplatesTitle,
    pageType,
    categories,
    refinements,
    searchReportKeyword,
    pathWithQueryParams
  });

  const fallbackType = getType(isContentPageOnly, isBrandPage, isHybrid);

  // ---- Robots Tag ---- //
  const isNoFollow = getRobotsTag(
    pathWithQueryParams, defaultCanonical, priceRangeExceptionCanonical, searchIndexingCanonical
  );

  // ---- Alternate Android Tag ---- //
  // Note: schemaBreadcrumbs isn't usable here for a Stand-Alone Brand Page — its first entry
  // is the "Brands" hub crumb, not the brand itself. The Android tag needs the actual brand
  // label, which only the raw crumb carries.
  const androidBreadcrumbs = isStandAloneBrandPage ? crumbs : schemaBreadcrumbs;
  const alternateAndroidTag = buildAndroidAlternateTag(androidBreadcrumbs, nValue);

  // ---- Extracted metadata with EMT overrides ---- //
  const {
    title = fallbackTitle,
    metaDescription = fallbackDescription,
    ogType = fallbackType,
    ogImage,
    schema
  } = filterEmtMetadataOverrides(emtMetadata);

  const normalizedTitle = decodeComparisonSymbols(title);
  const decodedMetaDescription = decodeComparisonSymbols(metaDescription);

  const isTitlePageValid = isTitleValid(normalizedTitle);

  const normalizedDescription = removeTrailingNewline(decodedMetaDescription);

  // ---- Schema ---- //
  const hasBreadcrumbs = Boolean(schemaBreadcrumbs?.length);
  const hasValidPageContext = isTitlePageValid && isCanonicalValid && hasBreadcrumbs;

  const parsedSchema = schema ? safeJsonParse(schema, {}) : null;
  const schemas = parsedSchema || (hasValidPageContext
    ? getSchemaTag(
      isContentPageOnly,
      schemaBreadcrumbs,
      normalizedTitle,
      canonical,
      emtDataContent?.questionAndAnswer,
      null,
      products
    )
    : null);

  // ---- Tags ----
  return generateTags({
    ...(isCanonicalValid && {
      canonical,
      ogUrl: canonical
    }),
    ...(isTitlePageValid && {
      title: normalizedTitle,
      ogTitle: normalizedTitle
    }),
    ogImage,
    description: normalizedDescription,
    ogType,
    ogDescription: normalizedDescription,
    ogSiteName: 'The Home Depot',
    fbAdmins: OPEN_GRAPH_FACEBOOK_ADMIN,
    robots: isNoFollow,
    alternate: alternateAndroidTag,
    schemas
  });
};

// BrowseSearchMetadata component
const BrowseSearchMetadata = (props) => {
  const {
    keyword: propKeyword = null,
    navParam: propNavParam = null,
    pathWithQueryParams: propPathWithQueryParams,
    variables: propVariables = null,
    seoGeneratedContentVariables: propSeoGeneratedContentVariables = null,
    pageWithFAQ,
    loading: propLoading,
    data: propData,
    error: propError
  } = props;

  const { customContext, customer } = useContext(ExperienceContext);
  const isB2BCustomerType = customer?.type === 'b2b';
  const { search: querystring, pathname } = useLocation();
  const path = `${pathname}${querystring}`;
  const pathWithQueryParams = getPreferredValue(propPathWithQueryParams, path);
  const isSearchPageIndexingEnabled = useConfigService('fs:isSearchPageIndexingEnabled');
  const storeId = useStoreId();
  const location = useLocation();
  let data;
  let error;
  let loading;

  const ctxVariables = getPreferredValue(customContext?.variables, {});
  const {
    navParam: contextNavParam,
    keyword: contextKeyword
  } = ctxVariables;

  const keyword = getPreferredValue(propKeyword, getKeyword(path), propVariables?.keyword);
  const navParam = getPreferredValue(propNavParam, contextNavParam);
  let searchVariables = propVariables || {
    keyword,
    navParam,
    storeId,
  };
  // eslint-disable-next-line
  const isControlledData = typeof propData !== 'undefined' || typeof propLoading !== 'undefined';
  const skipSearch = isControlledData || (!keyword && !navParam && !propVariables);

  const ctx = getPreferredValue(contextNavParam, contextKeyword);
  if (ctx && !isControlledData) {
    searchVariables = ctxVariables;
  }

  const searchOptions = {
    variables: searchVariables,
    skip: skipSearch,
  };
  ({
    data,
    loading,
    error,
  } = useDataModel('searchModel', searchOptions));

  const seoNavParam = getPreferredValue(
    propSeoGeneratedContentVariables?.navParam,
    searchVariables?.navParam
  );

  const nval = getNormalizedNavParam(seoNavParam);

  const decodedSearchTerm = isSearchPageIndexingEnabled
    ? decodeURIComponent(keyword || '').replace(/\s+/g, '-').toLowerCase()
    : '';
  // TODO : This is temporary logic until B2B gets it's own metadata subcomponent
  const shouldSkipEmtContent = isB2BCustomerType || (!nval && !decodedSearchTerm);
  const { data: emtData } = useDataModel('emtContent', {
    variables: {
      contentId: nval || decodedSearchTerm,
      pageName: 'plp'
    },
    skip: shouldSkipEmtContent
  });

  if (isControlledData) {
    data = propData;
    loading = propLoading;
    error = propError;
  }

  const helmetData = {
    ...data,
    emtData,
    location,
    isSearchPageIndexingEnabled
  };
  const helmetProps = {
    ...props,
    pathWithQueryParams
  };
  useHelmet('browseAndSearch',
    { data: helmetData, props: helmetProps, skipCleanup: true },
    browseHandler,
    [keyword, navParam, data?.searchModel]);
  return null;
};
BrowseSearchMetadata.displayName = 'BrowseSearchMetadata';

BrowseSearchMetadata.dataModel = extend(
  browseSearchModel,
  emtContentModel
);

BrowseSearchMetadata.propTypes = {
  keyword: PropTypes.string,
  seoGeneratedContentVariables: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({}),
    PropTypes.shape({ navParam: PropTypes.string })
  ]),
  navParam: PropTypes.string,
  pathWithQueryParams: PropTypes.string.isRequired,
  data: PropTypes.shape({}),
  error: PropTypes.shape({}),
  loading: PropTypes.bool,
  pageWithFAQ: PropTypes.bool,
  variables: PropTypes.shape({
    keyword: PropTypes.string
  })
};

const ErrorBoundBrowseAndSearchMetadata = withErrorBoundary(BrowseSearchMetadata);

export { ErrorBoundBrowseAndSearchMetadata as BrowseSearchMetadata };
